import type { PrimaryConnectionInfo } from "./classifyPrimaryConnection";

/**
 * PIPH-212: Render the bridge portion of a primary→primary connection sentence.
 * The caller splices the result into "Connected at {pos} {phrase} to {target}".
 *
 * Examples:
 *   direct + order 2          → "by double bond"
 *   direct + order 3          → "by triple bond"
 *   direct + order 1          → "by single bond"
 *   bridge (ester)            → "through ester"
 *   bridge (secondary amine)  → "through secondary amine"
 *   fallback (NH, order 1)    → "through single bond to NH"
 *   fallback (N, order 2)     → "through double bond to N"
 */
export function formatConnectionPhrase(info: PrimaryConnectionInfo): string {
    if (info.kind === "direct") {
        return `by ${bondOrderWord(info.bondOrder ?? 1)} bond`;
    }
    if (info.kind === "bridge") {
        // Strip "-A" / "-B" disambiguation suffixes and the "of {parent}" trail
        // so the phrase reads as "through ester" not "through ester-A of …".
        const name = stripDisplaySuffixes(info.group.name);
        return `through ${name}`;
    }
    // fallback
    const order = bondOrderWord(info.bondOrder ?? 1);
    const formula = info.formula ?? stripDisplaySuffixes(info.group.name);
    return `through ${order} bond to ${formula}`;
}

function bondOrderWord(order: 1 | 2 | 3): "single" | "double" | "triple" {
    if (order === 3) return "triple";
    if (order === 2) return "double";
    return "single";
}

function stripDisplaySuffixes(name: string): string {
    // Remove " of {parent}" tail (PIPH-57/61 naming convention).
    const ofIdx = name.indexOf(" of ");
    const trimmed = ofIdx > 0 ? name.substring(0, ofIdx) : name;
    // Remove trailing letter disambiguation like "-A".
    return trimmed.replace(/-[A-Z]+$/, "");
}
